import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faPenToSquare, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import toast from "react-hot-toast";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { FormCard, FormGrid } from "../../components/FormCard";
import { InputRow, SelectRow } from "../../components/Rows";
import DataTable from "../../components/DataTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import ModalBase from "../../components/ModalBase";
import ConfirmationModal from "../../components/ConfirmationModal";
import GridAreaTariffForm, {
    emptyTariffForm,
    formToLocalTariff,
    formToTariffDto,
    tariffToForm,
    tariffsHaveChanges,
} from "../../components/gridCost/GridAreaTariffForm";
import {
    isValidGridVoltageLevel,
    GRID_PEAK_POWER_HIGHEST_HOUR,
    getGridPeakPowerOptions,
    normalizePeakPowerMode,
} from "../../components/gridCost/gridCostConstants";
import { useTranslation } from "react-i18next";
import {
    formatDecimal,
    formatSpotAddonPercent,
    formatTariffValidFromMonthDisplay,
    formatTariffValidToMonthDisplay,
    validateTariffMonthRange,
} from "../../components/gridCost/gridCostUtils";
import { useAuth } from "../../providers/AuthContext";
import {
    createGridAreaTariff,
    deleteGridAreaTariff,
    getAllGridAreaTariffs,
    getGridArea,
    updateGridArea,
    updateGridAreaTariff,
} from "../../api/GridCost";

function cloneTariffs(list) {
    return (list || []).map((tariff) => ({ ...tariff }));
}

function findOriginalTariff(originalTariffs, tariff) {
    if (tariff.gridAreaTariffPublicId) {
        return originalTariffs.find(
            (item) => item.gridAreaTariffPublicId === tariff.gridAreaTariffPublicId
        );
    }
    return null;
}

function tariffNeedsPersist(tariff, originalTariffs) {
    if (tariff._isNew || !tariff.gridAreaTariffPublicId) {
        return true;
    }
    const original = findOriginalTariff(originalTariffs, tariff);
    if (!original) {
        return true;
    }
    return tariffsHaveChanges([tariff], [original]);
}

export default function EditGridAreaPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { getAccessToken } = useAuth();

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [area, setArea] = useState(null);
    const [areaForm, setAreaForm] = useState({
        gridAreaCode: "",
        areaName: "",
        companyName: "",
        peakPowerMode: GRID_PEAK_POWER_HIGHEST_HOUR,
    });
    const [originalAreaForm, setOriginalAreaForm] = useState(null);
    const [tariffs, setTariffs] = useState([]);
    const [originalTariffs, setOriginalTariffs] = useState([]);
    const [deletedTariffPublicIds, setDeletedTariffPublicIds] = useState([]);
    const [search, setSearch] = useState("");
    const [tariffModal, setTariffModal] = useState(null);
    const [confirmState, setConfirmState] = useState({ isOpen: false });

    useEffect(() => {
        setLoading(true);
        setArea(null);
        setAreaForm({
            gridAreaCode: "",
            areaName: "",
            companyName: "",
            peakPowerMode: GRID_PEAK_POWER_HIGHEST_HOUR,
        });
        setOriginalAreaForm(null);
        setTariffs([]);
        setOriginalTariffs([]);
        setDeletedTariffPublicIds([]);
        setSearch("");
        setTariffModal(null);
        setConfirmState({ isOpen: false });
    }, [id]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const token = await getAccessToken();
            const areaData = await getGridArea(id, token);
            setArea(areaData);
            const loadedForm = {
                gridAreaCode: areaData.gridAreaCode ?? "",
                areaName: areaData.areaName ?? "",
                companyName: areaData.companyName ?? "",
                peakPowerMode: normalizePeakPowerMode(areaData.peakPowerMode),
            };
            setAreaForm(loadedForm);
            setOriginalAreaForm(loadedForm);
            const tariffList = (await getAllGridAreaTariffs(token, id, null, null)) || [];
            setOriginalTariffs(cloneTariffs(tariffList));
            setTariffs(cloneTariffs(tariffList));
            setDeletedTariffPublicIds([]);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [getAccessToken, id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const hasAreaChanges = useMemo(() => {
        if (!originalAreaForm) {
            return false;
        }
        return (
            areaForm.gridAreaCode !== originalAreaForm.gridAreaCode ||
            areaForm.areaName !== originalAreaForm.areaName ||
            areaForm.companyName !== originalAreaForm.companyName ||
            areaForm.peakPowerMode !== originalAreaForm.peakPowerMode
        );
    }, [areaForm, originalAreaForm]);

    const canModifyArea = area?.canModify === true;
    const canAddOrgTariff = Boolean(area && canModifyArea);

    const canModifyTariffRow = useCallback((tariff) => {
        if (tariff?._isNew || !tariff?.gridAreaTariffPublicId) {
            return canAddOrgTariff;
        }
        return tariff.canModify === true;
    }, [canAddOrgTariff]);

    const hasSaveableChanges = useMemo(() => {
        if (canModifyArea && hasAreaChanges) {
            return true;
        }

        if (
            deletedTariffPublicIds.some((publicId) => {
                const original = originalTariffs.find((t) => t.gridAreaTariffPublicId === publicId);
                return original && canModifyTariffRow(original);
            })
        ) {
            return true;
        }

        return tariffs.some(
            (tariff) => tariffNeedsPersist(tariff, originalTariffs) && canModifyTariffRow(tariff)
        );
    }, [
        canModifyArea,
        hasAreaChanges,
        deletedTariffPublicIds,
        originalTariffs,
        tariffs,
        canModifyTariffRow,
    ]);

    const removeTariffLocally = (tariff) => {
        if (!canModifyTariffRow(tariff)) {
            return;
        }
        if (tariff.gridAreaTariffPublicId) {
            setDeletedTariffPublicIds((prev) => {
                if (prev.includes(tariff.gridAreaTariffPublicId)) {
                    return prev;
                }
                return [...prev, tariff.gridAreaTariffPublicId];
            });
        }

        setTariffs((prev) =>
            prev.filter((item) => {
                if (tariff.gridAreaTariffPublicId && item.gridAreaTariffPublicId === tariff.gridAreaTariffPublicId) {
                    return false;
                }
                if (tariff._clientKey && item._clientKey === tariff._clientKey) {
                    return false;
                }
                return true;
            })
        );
    };

    const confirmDeleteTariff = (tariff) => {
        if (!canModifyTariffRow(tariff)) {
            return;
        }
        setConfirmState({
            isOpen: true,
            title: t("grid.deleteTariff", { name: tariff.tariffName }),
            confirm: () => {
                removeTariffLocally(tariff);
                setConfirmState({ isOpen: false });
            },
        });
    };

    const handleApplyTariffModal = () => {
        if (!tariffModal?.form) {
            return;
        }
        const dto = formToTariffDto(tariffModal.form);
        if (!dto.tariffName || !dto.validFromUtc || !dto.gridAreaPublicId) {
            toast.error(t("grid.tariffFieldsRequired"));
            return;
        }

        const monthRangeError = validateTariffMonthRange(
            tariffModal.form.validFromMonth,
            tariffModal.form.validToMonth
        );
        if (monthRangeError) {
            toast.error(monthRangeError);
            return;
        }

        if (!isValidGridVoltageLevel(dto.gridVoltageLevel)) {
            toast.error(t("gridCost.voltageRequired"));
            return;
        }

        if (tariffModal.mode === "create") {
            setTariffs((prev) => [...prev, formToLocalTariff(tariffModal.form)]);
        } else {
            setTariffs((prev) =>
                prev.map((tariff) => {
                    const matchesPublicId =
                        tariff.gridAreaTariffPublicId &&
                        tariff.gridAreaTariffPublicId === tariffModal.tariffKey;
                    const matchesClientKey =
                        tariff._clientKey && tariff._clientKey === tariffModal.tariffKey;
                    if (!matchesPublicId && !matchesClientKey) {
                        return tariff;
                    }
                    return formToLocalTariff(tariffModal.form, tariff);
                })
            );
        }

        setTariffModal(null);
    };

    const handleSaveAll = async () => {
        if (!area || !hasSaveableChanges) {
            return;
        }

        setBusy(true);
        const toastId = toast.loading(t("toast.saving"));
        try {
            const token = await getAccessToken();

            for (const publicId of deletedTariffPublicIds) {
                const original = originalTariffs.find((t) => t.gridAreaTariffPublicId === publicId);
                if (!original || !canModifyTariffRow(original)) {
                    continue;
                }
                await deleteGridAreaTariff(publicId, token);
            }

            if (hasAreaChanges && canModifyArea) {
                await updateGridArea(
                    {
                        gridAreaPublicId: area.gridAreaPublicId,
                        gridAreaCode: areaForm.gridAreaCode.trim(),
                        areaName: areaForm.areaName.trim(),
                        companyName: areaForm.companyName.trim() || null,
                        peakPowerMode: areaForm.peakPowerMode || GRID_PEAK_POWER_HIGHEST_HOUR,
                        organizationId: area.organizationId ?? null,
                    },
                    token
                );
            }

            for (const tariff of tariffs) {
                if (!tariffNeedsPersist(tariff, originalTariffs)) {
                    continue;
                }

                if (!canModifyTariffRow(tariff)) {
                    continue;
                }

                if (!isValidGridVoltageLevel(tariff.gridVoltageLevel)) {
                    throw new Error(t("grid.tariffMissingVoltage", { name: tariff.tariffName }));
                }

                const dto = {
                    gridAreaPublicId: tariff.gridAreaPublicId || id,
                    gridVoltageLevel: tariff.gridVoltageLevel,
                    tariffName: tariff.tariffName,
                    validFromUtc: tariff.validFromUtc,
                    validToUtc: tariff.validToUtc || null,
                    fixedFeeMonthlySek: Number(tariff.fixedFeeMonthlySek ?? 0),
                    transferEnergySekPerKwh: Number(tariff.transferEnergySekPerKwh ?? 0),
                    transferSpotAddonPercent: Number(tariff.transferSpotAddonPercent ?? 0),
                    effectFeeSekPerKwMonth: Number(tariff.effectFeeSekPerKwMonth ?? 0),
                    subscriptionSekPerKwMonth: Number(tariff.subscriptionSekPerKwMonth ?? 0),
                    reactiveSekPerKvar: 0,
                };

                if (tariff._isNew || !tariff.gridAreaTariffPublicId) {
                    await createGridAreaTariff(dto, token);
                } else {
                    await updateGridAreaTariff(
                        {
                            ...dto,
                            gridAreaTariffPublicId: tariff.gridAreaTariffPublicId,
                        },
                        token
                    );
                }
            }

            toast.success(t("grid.areaSaved"), { id: toastId });
            await loadData();
        } catch (err) {
            toast.error(err.message, { id: toastId });
        } finally {
            setBusy(false);
        }
    };

    const openCreateTariff = () => {
        if (!canAddOrgTariff) {
            return;
        }
        setTariffModal({ mode: "create", form: emptyTariffForm(id), readOnly: false });
    };

    const openEditTariff = (tariff) => {
        const readOnly = !canModifyTariffRow(tariff);
        setTariffModal({
            mode: readOnly ? "view" : "edit",
            form: tariffToForm(tariff),
            tariffKey: tariff.gridAreaTariffPublicId || tariff._clientKey,
            readOnly,
        });
    };

    const tariffColumns = [
        {
            key: "tariffName",
            header: t("form.tariffName"),
            sortable: true,
            getSortValue: (row) => row.tariffName ?? "",
            render: (row) => (
                <span>
                    {row.tariffName || "—"}
                    {(row._isNew || tariffNeedsPersist(row, originalTariffs)) && (
                        <span className="ml-2 text-xs text-amber-700">{t("common.unsaved")}</span>
                    )}
                </span>
            ),
        },
        {
            key: "gridVoltageLevel",
            header: t("table.voltage"),
            sortable: true,
            getSortValue: (row) => row.gridVoltageLevel ?? "",
            render: (row) =>
                row.gridVoltageLevel
                    ? t(`grid.voltage.${row.gridVoltageLevel}`, { defaultValue: row.gridVoltageLevel })
                    : "",
        },
        {
            key: "validFromUtc",
            header: t("table.validFrom"),
            sortable: true,
            getSortValue: (row) => row.validFromUtc ?? "",
            render: (row) => formatTariffValidFromMonthDisplay(row.validFromUtc),
        },
        {
            key: "validToUtc",
            header: t("table.validTo"),
            sortable: true,
            getSortValue: (row) => row.validToUtc ?? "",
            render: (row) => formatTariffValidToMonthDisplay(row.validToUtc),
        },
        {
            key: "fixedFeeMonthlySek",
            header: t("table.fixedFee"),
            sortable: true,
            getSortValue: (row) => row.fixedFeeMonthlySek ?? 0,
            render: (row) => `${formatDecimal(row.fixedFeeMonthlySek, 2)} ${t("gridCost.unitSekMonth")}`,
        },
        {
            key: "transferEnergySekPerKwh",
            header: t("table.transferEnergy"),
            sortable: true,
            getSortValue: (row) => row.transferEnergySekPerKwh ?? 0,
            render: (row) => `${formatDecimal(row.transferEnergySekPerKwh, 4)} ${t("gridCost.unitSekKwh")}`,
        },
        {
            key: "transferSpotAddonPercent",
            header: t("table.spotAddOn"),
            sortable: true,
            getSortValue: (row) => row.transferSpotAddonPercent ?? 0,
            render: (row) => formatSpotAddonPercent(row.transferSpotAddonPercent),
        },
        {
            key: "effectFeeSekPerKwMonth",
            header: t("table.capacityFee"),
            sortable: true,
            getSortValue: (row) => row.effectFeeSekPerKwMonth ?? 0,
            render: (row) => `${formatDecimal(row.effectFeeSekPerKwMonth, 2)} ${t("gridCost.unitSekKwMonth")}`,
        },
        {
            key: "subscriptionSekPerKwMonth",
            header: t("table.subscription"),
            sortable: true,
            getSortValue: (row) => row.subscriptionSekPerKwMonth ?? 0,
            render: (row) => `${formatDecimal(row.subscriptionSekPerKwMonth, 2)} ${t("gridCost.unitSekKwMonth")}`,
        },
        {
            key: "actions",
            header: "",
            sortable: false,
            searchable: false,
            rawCell: true,
            headerClassName: "min-w-[88px]",
            render: (row) => (
                <td className="text-right">
                    <div className="flex justify-end items-center gap-4">
                        <button
                            type="button"
                            className="text-primary-600 hover:text-primary-800 transition-colors"
                            title={canModifyTariffRow(row) ? t("common.edit") : t("common.view")}
                            onClick={() => openEditTariff(row)}
                        >
                            <FontAwesomeIcon icon={faPenToSquare} className="w-4" />
                        </button>
                        {canModifyTariffRow(row) ? (
                            <button
                                type="button"
                                className="text-error-500 hover:text-error-600 transition-colors"
                                title={t("common.delete")}
                                onClick={() => confirmDeleteTariff(row)}
                            >
                                <FontAwesomeIcon icon={faTrash} className="w-4" />
                            </button>
                        ) : null}
                    </div>
                </td>
            ),
        },
    ];

    if (loading) {
        return (
            <>
                <HeaderBar />
                <div className="flex w-full min-h-0">
                    <Sidebar />
                    <main className="page flex items-center justify-center min-h-[50vh]">
                        <LoadingSpinner text={t("common.loadingEntity", { name: t("entity.gridArea") })} />
                    </main>
                </div>
            </>
        );
    }

    return (
        <>
            <HeaderBar />
            <div className="flex w-full min-h-0">
                <Sidebar />
                <main className="page">
                    {area ? (
                        <div className="page-content">
                            <div className="flex flex-row items-center pb-4">
                                <button
                                    type="button"
                                    onClick={() => navigate("/grid-areas")}
                                    className="text-black flex"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                                </button>
                                <h2 className="border-l border-mute-200 pl-4">
                                    {areaForm.gridAreaCode} — {areaForm.areaName}
                                </h2>
                                <div className="flex gap-4 ml-auto">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => navigate("/grid-areas")}
                                    >
                                        {canModifyArea || hasSaveableChanges ? t("common.cancel") : t("common.back")}
                                    </button>
                                    {canModifyArea || hasSaveableChanges ? (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={
                                                busy ||
                                                !hasSaveableChanges ||
                                                (canModifyArea &&
                                                    (!areaForm.gridAreaCode.trim() ||
                                                        !areaForm.areaName.trim()))
                                            }
                                            onClick={handleSaveAll}
                                        >
                                            {t("common.save")}
                                        </button>
                                    ) : null}
                                </div>
                            </div>

                            {!canModifyArea ? (
                                <p className="text-sm text-mute-600 mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                                    {t("grid.readOnlyShared")}
                                </p>
                            ) : null}

                            <FormCard title={t("entity.gridArea")} className="mb-8">
                                <FormGrid>
                                    <InputRow
                                        label={t("common.code")}
                                        value={areaForm.gridAreaCode}
                                        disabled={!canModifyArea}
                                        onChange={(value) =>
                                            setAreaForm((p) => ({ ...p, gridAreaCode: value }))
                                        }
                                    />
                                    <InputRow
                                        label={t("common.name")}
                                        value={areaForm.areaName}
                                        disabled={!canModifyArea}
                                        onChange={(value) =>
                                            setAreaForm((p) => ({ ...p, areaName: value }))
                                        }
                                    />
                                    <InputRow
                                        label={t("common.company")}
                                        value={areaForm.companyName}
                                        disabled={!canModifyArea}
                                        onChange={(value) =>
                                            setAreaForm((p) => ({ ...p, companyName: value }))
                                        }
                                    />
                                    <SelectRow
                                        label={t("form.peakPower")}
                                        tooltip={t("grid.peakPowerTooltip")}
                                        value={areaForm.peakPowerMode}
                                        options={getGridPeakPowerOptions(t)}
                                        disabled={!canModifyArea}
                                        onChange={(value) =>
                                            setAreaForm((p) => ({
                                                ...p,
                                                peakPowerMode:
                                                    value || GRID_PEAK_POWER_HIGHEST_HOUR,
                                            }))
                                        }
                                    />
                                </FormGrid>
                            </FormCard>

                            <FormCard>
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                                    <h2 className="text-xl font-bold">{t("form.gridTariffs")}</h2>
                                    {canAddOrgTariff ? (
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={openCreateTariff}
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                            {t("grid.newTariff")}
                                        </button>
                                    ) : null}
                                </div>
                                <DataTable
                                    rows={tariffs}
                                    columns={tariffColumns}
                                    search={search}
                                    onSearchChange={setSearch}
                                    searchPlaceholder={t("search.tariffs")}
                                    defaultSortColumn="tariffName"
                                    rowKey={(row) => row.gridAreaTariffPublicId || row._clientKey}
                                    emptyColSpan={10}
                                    tableMinWidthClassName="min-w-[1200px]"
                                />
                            </FormCard>
                        </div>
                    ) : null}
                </main>
            </div>

            <ModalBase
                isOpen={Boolean(tariffModal)}
                onClose={() => setTariffModal(null)}
                size="lg"
                className="overflow-y-auto max-h-[90vh]"
                closeOnOutsideClick={!busy}
            >
                {tariffModal && (
                    <>
                        <GridAreaTariffForm
                            inModal
                            readOnly={tariffModal.readOnly}
                            form={tariffModal.form}
                            setForm={(updater) => {
                                setTariffModal((prev) => ({
                                    ...prev,
                                    form: typeof updater === "function" ? updater(prev.form) : updater,
                                }));
                            }}
                        />
                        <div className="flex gap-3 mt-6 justify-end">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => setTariffModal(null)}
                            >
                                {tariffModal.readOnly ? t("common.close") : t("common.cancel")}
                            </button>
                            {!tariffModal.readOnly ? (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleApplyTariffModal}
                                >
                                    {tariffModal.mode === "create" ? t("common.add") : t("common.update")}
                                </button>
                            ) : null}
                        </div>
                    </>
                )}
            </ModalBase>

            <ConfirmationModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                onClose={() => setConfirmState({ isOpen: false })}
                onConfirm={confirmState.confirm}
            />
        </>
    );
}
